import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, orderBy, onSnapshot, getDoc, doc, updateDoc, addDoc } from 'firebase/firestore';
import { UserProfile } from '../types';
import { X, CheckCircle, XCircle } from './icons';

interface FloatingMeetingNotificationProps {
  userProfile: UserProfile | null;
}

interface MeetingChangeNotification {
  id: string;
  meetingId: string;
  changeRequestId: string;
  title: string;
  oldStartTime: Date;
  newStartTime: Date;
  reason: string;
  fromUserName: string;
}

const FloatingMeetingNotification: React.FC<FloatingMeetingNotificationProps> = ({ userProfile }) => {
  const [notification, setNotification] = useState<MeetingChangeNotification | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  useEffect(() => {
    if (!userProfile?.uid) return;

    // Listen for meeting change request notifications
    // Note: Using multiple where clauses - may need composite index
    const inboxQuery = query(
      collection(db, 'inboxItems'),
      where('userId', '==', userProfile.uid),
      where('category', '==', 'meeting'),
      where('isRead', '==', false),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(inboxQuery, (snapshot) => {
      // Filter for meeting change requests in the client (title filter removed from query to avoid composite index requirement)
      const changeRequests = snapshot.docs
        .map(doc => ({ id: doc.id, ...(doc.data() as any) }))
        .filter((item: any) => item.title === '📅 Meeting wijzigingsverzoek' && item.actionData?.changeRequestId);
      
      if (changeRequests.length > 0) {
        const latestItem = changeRequests[0];
        const metadata = latestItem.metadata || {};
        
        setNotification({
          id: latestItem.id,
          meetingId: latestItem.actionData?.meetingId || '',
          changeRequestId: latestItem.actionData?.changeRequestId || '',
          title: latestItem.message || 'Meeting wijzigingsverzoek',
          oldStartTime: metadata.oldStartTime?.toDate ? metadata.oldStartTime.toDate() : new Date(metadata.oldStartTime),
          newStartTime: metadata.newStartTime?.toDate ? metadata.newStartTime.toDate() : new Date(metadata.newStartTime),
          reason: metadata.reason || '',
          fromUserName: metadata.senderName || 'Iemand'
        });
        setIsMinimized(false);
      } else {
        setNotification(null);
      }
    }, (error) => {
      console.error('Error in meeting change request listener:', error);
    });

    return () => unsubscribe();
  }, [userProfile]);

  const handleAccept = async () => {
    if (!notification || !userProfile) return;

    try {
      // Get meeting event
      const meetingDoc = await getDoc(doc(db, 'calendarEvents', notification.meetingId));
      if (!meetingDoc.exists()) {
        alert('❌ Meeting niet gevonden');
        return;
      }

      const meetingData = meetingDoc.data();
      const pendingChangeRequest = meetingData.pendingChangeRequest;

      // Accept change - update meeting time
      await updateDoc(doc(db, 'calendarEvents', notification.meetingId), {
        startTime: pendingChangeRequest.newStartTime,
        endTime: pendingChangeRequest.newEndTime,
        'pendingChangeRequest.status': 'accepted',
        updatedAt: new Date()
      });

      // Mark inbox item as read
      await updateDoc(doc(db, 'inboxItems', notification.id), {
        isRead: true
      });

      // Create confirmation for requester
      const requesterId = pendingChangeRequest.fromUserId;
      await addDoc(collection(db, 'inboxItems'), {
        userId: requesterId,
        type: 'confirmation',
        category: 'meeting',
        title: '✅ Meeting wijziging geaccepteerd',
        message: `${userProfile.displayName || 'Iemand'} heeft je wijzigingsverzoek geaccepteerd.${responseMessage ? `\n\nBericht: ${responseMessage}` : ''}`,
        isRead: false,
        priority: 'high',
        createdAt: new Date(),
        metadata: {
          eventDate: pendingChangeRequest.newStartTime?.toDate ? pendingChangeRequest.newStartTime.toDate() : new Date(pendingChangeRequest.newStartTime)
        }
      });

      setNotification(null);
      setResponseMessage('');
      alert('✅ Wijziging geaccepteerd!');
    } catch (error) {
      console.error('Error accepting change:', error);
      alert('❌ Fout bij accepteren wijziging');
    }
  };

  const handleDecline = async () => {
    if (!notification || !userProfile) return;

    try {
      // Get meeting event
      const meetingDoc = await getDoc(doc(db, 'calendarEvents', notification.meetingId));
      if (!meetingDoc.exists()) {
        alert('❌ Meeting niet gevonden');
        return;
      }

      const meetingData = meetingDoc.data();
      const pendingChangeRequest = meetingData.pendingChangeRequest;

      // Decline change
      await updateDoc(doc(db, 'calendarEvents', notification.meetingId), {
        'pendingChangeRequest.status': 'declined',
        updatedAt: new Date()
      });

      // Mark inbox item as read
      await updateDoc(doc(db, 'inboxItems', notification.id), {
        isRead: true
      });

      // Create notification for requester
      const requesterId = pendingChangeRequest.fromUserId;
      await addDoc(collection(db, 'inboxItems'), {
        userId: requesterId,
        type: 'notification',
        category: 'meeting',
        title: '❌ Meeting wijziging geweigerd',
        message: `${userProfile.displayName || 'Iemand'} heeft je wijzigingsverzoek geweigerd.${responseMessage ? `\n\nReden: ${responseMessage}` : ''}`,
        isRead: false,
        priority: 'medium',
        createdAt: new Date()
      });

      setNotification(null);
      setResponseMessage('');
      alert('❌ Wijziging geweigerd');
    } catch (error) {
      console.error('Error declining change:', error);
      alert('❌ Fout bij weigeren wijziging');
    }
  };

  if (!notification) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] max-w-sm w-full px-3 sm:px-0">
      <div className={`bg-white shadow-2xl rounded-2xl border-2 border-orange-300 overflow-hidden transition-all duration-300 ${
        isMinimized ? 'h-16' : ''
      }`}>
        {/* Header - Always visible */}
        <div 
          className="px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-400 text-white flex items-center justify-between cursor-pointer"
          onClick={() => setIsMinimized(!isMinimized)}
        >
          <div className="flex items-center space-x-2">
            <span className="text-lg animate-pulse">📅</span>
            <div className="text-sm font-semibold">
              Meeting Wijzigingsverzoek
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setNotification(null);
              }}
              className="text-white/80 hover:text-white text-xs px-2 py-1 rounded-md hover:bg-white/10"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Content - Collapsible */}
        {!isMinimized && (
          <div className="px-4 py-3 space-y-3">
            <div className="text-sm text-gray-800">
              <div className="font-medium mb-2">{notification.fromUserName} wil de meeting verzetten:</div>
              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs space-y-1">
                <div><strong>Huidige tijd:</strong> {notification.oldStartTime.toLocaleString('nl-NL')}</div>
                <div><strong>Nieuwe tijd:</strong> {notification.newStartTime.toLocaleString('nl-NL')}</div>
                {notification.reason && (
                  <div className="mt-2 italic text-gray-600">"{notification.reason}"</div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Bericht (optioneel)
              </label>
              <textarea
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                placeholder="Voeg een bericht toe..."
                rows={2}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div className="flex space-x-2">
              <button
                onClick={handleAccept}
                className="flex-1 px-3 py-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition-colors font-medium flex items-center justify-center space-x-1"
              >
                <CheckCircle size={16} />
                <span>Accepteren</span>
              </button>
              <button
                onClick={handleDecline}
                className="flex-1 px-3 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors font-medium flex items-center justify-center space-x-1"
              >
                <XCircle size={16} />
                <span>Weigeren</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FloatingMeetingNotification;


